/**
 * Democratic Services Theme - Main JavaScript
 *
 * @since 2.0.0
 */

/* global gtag */

// Import additional modules
import './meeting-actions.js';
import './meeting-calendar.js';
import './meeting-search.js';
import './voice-search.js';
import './committee-tabs.js';

(function () {
	'use strict';

	/**
	 * Navigation toggle for mobile
	 */
	const initMobileNav = () => {
		const menuToggle = document.querySelector('.menu-toggle');
		const navMenu = document.querySelector('.nav-menu');

		if (!menuToggle || !navMenu) {
			return;
		}

		menuToggle.addEventListener('click', () => {
			navMenu.classList.toggle('toggled');
			const expanded = menuToggle.getAttribute('aria-expanded') === 'true';
			menuToggle.setAttribute('aria-expanded', !expanded);
		});
	};

	/**
	 * Initialize meeting cards hover effects
	 */
	const initMeetingCards = () => {
		const cards = document.querySelectorAll(
			'.meeting-card, .committee-card, .member-card',
		);

		cards.forEach((card) => {
			const link = card.querySelector('a');
			if (!link) return;

			card.style.cursor = 'pointer';
			card.addEventListener('click', (e) => {
				if (e.target.tagName !== 'A') {
					link.click();
				}
			});
		});
	};

	/**
	 * Initialize document download tracking
	 */
	const initDocumentTracking = () => {
		const downloadLinks = document.querySelectorAll('a[data-document-id]');

		downloadLinks.forEach((link) => {
			link.addEventListener('click', () => {
				const documentId = link.dataset.documentId;

				// Track download if analytics available
				if (typeof gtag !== 'undefined') {
					gtag('event', 'download', {
						event_category: 'Documents',
						event_label: documentId,
					});
				}
			});
		});
	};

	/**
	 * Initialize search form enhancements
	 */
	const initSearchForm = () => {
		const searchForms = document.querySelectorAll('.search-form');

		searchForms.forEach((form) => {
			const input = form.querySelector('input[type="search"]');
			if (!input) return;

			// Add clear button functionality
			input.addEventListener('input', () => {
				form.classList.toggle('has-value', input.value.length > 0);
			});
		});
	};

	/**
	 * Initialize petition signature form
	 */
	const initPetitionForm = () => {
		const form = document.querySelector('.petition-sign-form');
		if (!form) return;

		form.addEventListener('submit', async (e) => {
			e.preventDefault();

			const submitBtn = form.querySelector('button[type="submit"]');
			const originalText = submitBtn.textContent;
			submitBtn.textContent = 'Signing...';
			submitBtn.disabled = true;

			try {
				const formData = new FormData(form);
				const response = await fetch(form.action, {
					method: 'POST',
					body: formData,
					headers: {
						'X-WP-Nonce': window.democraticServices?.nonce || '',
					},
				});

				const data = await response.json();

				if (data.success) {
					form.innerHTML = `
                        <div class="petition-success">
                            <p>Thank you for signing this petition!</p>
                            <p>A confirmation email has been sent to your address.</p>
                        </div>
                    `;
				} else {
					throw new Error(data.message || 'Failed to sign petition');
				}
			} catch (error) {
				submitBtn.textContent = originalText;
				submitBtn.disabled = false;
				// Display error to user
				const errorDiv = document.createElement('div');
				errorDiv.className = 'petition-error';
				errorDiv.textContent = error.message;
				// Insert safely: if submitBtn is a child of the form, insert before it;
				// otherwise prepend to the form to avoid NotFoundError.
				if (submitBtn && submitBtn.parentNode === form) {
					form.insertBefore(errorDiv, submitBtn);
				} else if (form.firstChild) {
					form.insertBefore(errorDiv, form.firstChild);
				} else {
					form.appendChild(errorDiv);
				}
			}
		});
	};

	/**
	 * Initialize consultation response form
	 */
	const initConsultationForm = () => {
		const form = document.querySelector('.consultation-response-form');
		if (!form) return;

		// Character counter for textareas
		const textareas = form.querySelectorAll('textarea[maxlength]');
		textareas.forEach((textarea) => {
			const maxLength = textarea.getAttribute('maxlength');
			const counter = document.createElement('span');
			counter.className = 'char-counter';
			counter.textContent = `0 / ${maxLength}`;
			textarea.parentNode.appendChild(counter);

			textarea.addEventListener('input', () => {
				counter.textContent = `${textarea.value.length} / ${maxLength}`;
			});
		});
	};

	/**
	 * Initialize accessibility enhancements
	 */
	const initAccessibility = () => {
		// Skip link functionality
		const skipLink = document.querySelector('.skip-link');
		if (skipLink) {
			skipLink.addEventListener('click', () => {
				const target = document.querySelector(skipLink.getAttribute('href'));
				if (target) {
					target.setAttribute('tabindex', '-1');
					target.focus();
				}
			});
		}

		// Focus visible class
		document.addEventListener('keydown', (e) => {
			if (e.key === 'Tab') {
				document.body.classList.add('keyboard-navigation');
			}
		});

		document.addEventListener('mousedown', () => {
			document.body.classList.remove('keyboard-navigation');
		});
	};

	/**
	 * Initialize all functionality on DOM ready
	 */
	const init = () => {
		initMobileNav();
		initMeetingCards();
		initDocumentTracking();
		initSearchForm();
		initPetitionForm();
		initConsultationForm();
		initAccessibility();
	};

	// Run on DOM ready
	if (document.readyState === 'loading') {
		document.addEventListener('DOMContentLoaded', init);
	} else {
		init();
	}
})();
